import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  UserGroupIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ShareIcon,
  GiftIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { Card, Button } from './common';
import confetti from 'canvas-confetti';

// Milestone definitions for gamification
const MILESTONES = [
  { referrals: 1, reward: 7, badge: 'First Referral', emoji: '🎉' },
  { referrals: 5, reward: 35, badge: 'Connector', emoji: '🤝' },
  { referrals: 10, reward: 70, badge: 'Influencer', emoji: '⭐' },
  { referrals: 25, reward: 175, badge: 'Ambassador', emoji: '🏆' }
];

const ReferralDashboard = ({ user }) => {
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const referralCode = user?.referral?.code || '';
  const referralCount = user?.referral?.count || 0;
  const freeDaysAvailable = user?.referral?.freeDaysAvailable || 0;
  const freeDaysEarned = referralCount * 7; // 7 days per referral

  const referralLink = `https://kwikly.me/register?ref=${referralCode}`;

  // Calculate progress to next milestone
  const currentMilestone = MILESTONES.filter(m => referralCount >= m.referrals).pop();
  const nextMilestone = MILESTONES.find(m => referralCount < m.referrals);
  const progressToNext = nextMilestone
    ? Math.min(100, (referralCount / nextMilestone.referrals) * 100)
    : 100;

  // Check if user just hit a milestone (would come from backend in real implementation)
  useEffect(() => {
    const hitMilestone = MILESTONES.find(m => m.referrals === referralCount);
    if (hitMilestone && referralCount > 0) {
      setShowConfetti(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [referralCount]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Join Kwikly - Beat ATS in 60 seconds',
      text: 'I use Kwikly to tailor my resume for each job in 60 seconds. Get 7 free days of Pro when you sign up!',
      url: referralLink
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error - fall back to copy
        if (err.name !== 'AbortError') {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  if (!referralCode) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-teal-50 via-cyan-50 to-blue-50 border-teal-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 p-3 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg shadow-lg">
            <GiftIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-teal-900 flex items-center gap-2">
              Refer Friends, Get Free Pro Days
              {currentMilestone && (
                <span className="text-lg">{currentMilestone.emoji}</span>
              )}
            </h3>
            <p className="text-sm text-teal-700">
              Earn 7 days of Pro for each friend who signs up
            </p>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white/60 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <UserGroupIcon className="h-4 w-4 text-teal-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{referralCount}</p>
          <p className="text-xs text-gray-600">Referrals</p>
        </div>
        <div className="bg-white/60 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <StarIcon className="h-4 w-4 text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">{freeDaysEarned}</p>
          <p className="text-xs text-gray-600">Days Earned</p>
        </div>
        <div className="bg-white/60 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <GiftIcon className="h-4 w-4 text-primary-600" />
          </div>
          <p className="text-2xl font-bold text-primary-600">{freeDaysAvailable}</p>
          <p className="text-xs text-gray-600">Available</p>
        </div>
      </div>

      {/* Progress to Next Milestone */}
      {nextMilestone && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress to {nextMilestone.badge}</span>
            <span>{referralCount} / {nextMilestone.referrals}</span>
          </div>
          <div className="h-2 bg-white/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full transition-all duration-500"
              style={{ width: `${progressToNext}%` }}
            />
          </div>
          <p className="text-xs text-teal-700 mt-1">
            {nextMilestone.referrals - referralCount} more to unlock {nextMilestone.emoji} {nextMilestone.badge} ({nextMilestone.reward} total days)
          </p>
        </div>
      )}

      {/* Earned Badges */}
      {currentMilestone && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">Earned:</span>
          {MILESTONES.filter(m => referralCount >= m.referrals).map((milestone) => (
            <span
              key={milestone.referrals}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-100 to-amber-200 rounded-full text-xs font-medium text-amber-800"
            >
              {milestone.emoji} {milestone.badge}
            </span>
          ))}
        </div>
      )}

      {/* Referral Link */}
      <div className="bg-white/80 rounded-lg p-3 mb-4">
        <p className="text-xs text-gray-600 mb-2">Your referral link:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white px-3 py-2 rounded border border-gray-200 text-sm text-gray-700 truncate">
            {referralLink}
          </code>
          <Button
            size="sm"
            variant={copied ? 'secondary' : 'primary'}
            onClick={handleCopy}
            className="flex-shrink-0"
          >
            {copied ? (
              <>
                <CheckIcon className="h-4 w-4 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Share Button */}
      <Button
        variant="primary"
        onClick={handleShare}
        className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
      >
        <ShareIcon className="h-4 w-4 mr-2" />
        Share with Friends
      </Button>

      {/* Viral Hook */}
      <p className="text-xs text-center text-teal-700 mt-3">
        Your friends also get 7 free Pro days when they sign up!
      </p>
    </Card>
  );
};

ReferralDashboard.propTypes = {
  user: PropTypes.shape({
    referral: PropTypes.shape({
      code: PropTypes.string,
      count: PropTypes.number,
      freeDaysAvailable: PropTypes.number
    })
  })
};

export default ReferralDashboard;
